/**
 * Unified Realtime Sync
 * ---------------------
 * One Supabase Realtime channel per critical table.
 * On every INSERT/UPDATE/DELETE we invalidate the matching React Query keys
 * so every device (desktop web, mobile web, Android, iOS, tablet) sees the
 * exact same data in real time — no platform branching.
 */
import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

type TableName =
  | "bookings"
  | "payments"
  | "chat_messages"
  | "push_notifications"
  | "properties"
  | "stay_offers"
  | "favorites"
  | "host_balances"
  | "affiliate_transactions"
  | "reviews"
  | "profiles"
  | "user_roles"
  | "platform_settings"
  | "insurance_requests"
  | "insurance_policies";

/**
 * Map each watched table to the React Query keys it should invalidate.
 * Keep keys broad — `invalidateQueries({ queryKey: [key] })` matches any
 * query whose key starts with that prefix.
 */
const TABLE_TO_KEYS: Record<TableName, string[]> = {
  bookings: ["bookings", "my-bookings", "host-bookings", "booking-details", "dashboard"],
  payments: ["payments", "booking-details", "host-financials", "admin-payments"],
  chat_messages: ["chat", "messages", "booking-chat"],
  push_notifications: ["notifications", "push-notifications", "notification-bell"],
  properties: ["properties", "property", "host-properties", "explore", "favorites"],
  stay_offers: ["stay-offers", "offers", "offers-received", "my-offers"],
  favorites: ["favorites"],
  host_balances: ["host-balance", "host-financials"],
  affiliate_transactions: ["affiliate-transactions", "affiliate-balance"],
  reviews: ["reviews", "property", "host-score"],
  profiles: ["profile", "user-profile", "profiles", "host-profile"],
  user_roles: ["user-roles", "roles", "profile"],
  platform_settings: ["platform-settings", "settings", "admin-settings", "registration-settings"],
  insurance_requests: ["insurance", "insurance-requests", "my-insurance"],
  insurance_policies: ["insurance", "insurance-policies"],
};

let channels: RealtimeChannel[] = [];
let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
const pendingKeys = new Set<string>();

function scheduleInvalidate(qc: QueryClient, keys: string[]) {
  keys.forEach((k) => pendingKeys.add(k));
  if (invalidateTimer) return;
  invalidateTimer = setTimeout(() => {
    const keysToInvalidate = Array.from(pendingKeys);
    pendingKeys.clear();
    invalidateTimer = null;
    keysToInvalidate.forEach((key) => {
      qc.invalidateQueries({ queryKey: [key] });
    });
  }, 120);
}

/**
 * Start realtime sync for the current session.
 * Safe to call multiple times — it tears down previous channels first.
 */
export function startRealtimeSync(queryClient: QueryClient) {
  stopRealtimeSync();

  (Object.keys(TABLE_TO_KEYS) as TableName[]).forEach((table) => {
    const channel = supabase
      .channel(`sync:${table}`)
      .on(
        "postgres_changes" as any,
        { event: "*", schema: "public", table },
        () => {
          scheduleInvalidate(queryClient, TABLE_TO_KEYS[table]);
        }
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.warn(`[realtimeSync] channel ${table} ${status} — will auto-reconnect`);
        }
      });

    channels.push(channel);
  });

  console.log(`[realtimeSync] subscribed to ${channels.length} tables`);
}

export function stopRealtimeSync() {
  channels.forEach((c) => {
    try {
      supabase.removeChannel(c);
    } catch {
      // ignore
    }
  });
  channels = [];
}

/**
 * Force-invalidate every watched entity. Used on reconnect / app resume
 * / window focus to recover from any missed event.
 */
export function invalidateAll(queryClient: QueryClient) {
  Object.values(TABLE_TO_KEYS)
    .flat()
    .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
}
